import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import {
    Bell, ShieldWarning, ArrowsClockwise, ArrowRight, X,
    CaretDown, CaretUp, BellSlash, PaperPlaneTilt,
} from "@phosphor-icons/react";

/**
 * Planner portal notification bell.
 *
 * Mirrors the doctor-side `<NotificationBell>` UX (grouping, snooze for
 * 1h, click-to-open) so the "any submission lands on a bell" pattern is
 * universal across portals. Reads `GET /api/coe/planner/notifications`
 * every 60s and groups cases by `kind`.
 *
 * Surfaced kinds:
 *   - assigned_to_planner  — Admin routed a new case to me
 *   - revision_requested   — Doctor asked for plan changes
 *   - refinement_requested — Doctor opened a refinement round
 */
const SNOOZE_MS = 60 * 60 * 1000;                       // 1 hour
const SNOOZE_KEY = "planner.notif.snoozed_until";


function getSnoozedUntil() {
    try {
        const v = Number(window.localStorage.getItem(SNOOZE_KEY));
        return v && v > Date.now() ? v : 0;
    } catch { return 0; }
}


const KIND_META = {
    assigned_to_planner: {
        label: "New cases to plan",
        Icon: PaperPlaneTilt,
        chipClass: "bg-violet-100 text-violet-700",
        target: "/coe/planner/queue?bucket=pending_acceptance&sort=oldest",
    },
    revision_requested: {
        label: "Plan revisions requested",
        Icon: ShieldWarning,
        chipClass: "bg-amber-100 text-amber-700",
        target: "/coe/planner/queue?bucket=plan_revision&sort=oldest",
    },
    refinement_requested: {
        label: "Refinements requested",
        Icon: ArrowsClockwise,
        chipClass: "bg-amber-100 text-amber-700",
        target: "/coe/planner/queue?bucket=refinement&sort=oldest",
    },
};


export default function PlannerNotificationBell() {
    const [open, setOpen] = useState(false);
    const [data, setData] = useState({ total: 0, items: [] });
    const [loading, setLoading] = useState(true);
    const [snoozedUntil, setSnoozedUntil] = useState(getSnoozedUntil());
    const [expanded, setExpanded] = useState({});
    const ref = useRef(null);
    const navigate = useNavigate();

    const load = () => {
        api.get("/coe/planner/notifications")
            .then((r) => setData(r.data))
            .catch(() => {})
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        load();
        const t = setInterval(load, 60000);
        return () => clearInterval(t);
    }, []);

    useEffect(() => {
        const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", close);
        return () => document.removeEventListener("mousedown", close);
    }, []);

    useEffect(() => {
        const t = setInterval(() => setSnoozedUntil(getSnoozedUntil()), 60000);
        return () => clearInterval(t);
    }, []);

    const goTo = (target) => {
        setOpen(false);
        navigate(target);
    };

    const groups = useMemo(() => {
        const map = new Map();
        for (const n of data.items || []) {
            const k = n.kind || "assigned_to_planner";
            if (!map.has(k)) map.set(k, []);
            map.get(k).push(n);
        }
        return Array.from(map.entries())
            .map(([kind, items]) => ({ kind, items }))
            .sort((a, b) => b.items.length - a.items.length);
    }, [data.items]);

    const liveCount = data.total || 0;
    const snoozedNow = snoozedUntil > Date.now();
    const visibleCount = snoozedNow ? 0 : liveCount;

    const markAllRead = () => {
        const until = Date.now() + SNOOZE_MS;
        try { window.localStorage.setItem(SNOOZE_KEY, String(until)); } catch (e) { /* storage unavailable */ }
        setSnoozedUntil(until);
        setOpen(false);
    };

    const unsnooze = () => {
        try { window.localStorage.removeItem(SNOOZE_KEY); } catch (e) { /* storage unavailable */ }
        setSnoozedUntil(0);
    };

    return (
        <div className="relative" ref={ref}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="relative w-9 h-9 rounded-full grid place-items-center hover:bg-secondary transition-colors"
                aria-label={`Notifications, ${visibleCount} item${visibleCount === 1 ? "" : "s"}`}
                data-testid="planner-notification-bell"
            >
                <Bell size={18} weight={visibleCount > 0 ? "fill" : "regular"} className={visibleCount > 0 ? "text-primary" : "text-muted-foreground"} />
                {visibleCount > 0 && (
                    <span
                        className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-600 text-white text-[10px] font-bold tabular-nums grid place-items-center border-2 border-card"
                        data-testid="planner-notification-bell-badge"
                    >
                        {visibleCount > 99 ? "99+" : visibleCount}
                    </span>
                )}
            </button>

            {open && (
                <div
                    className="absolute right-0 mt-2 w-[380px] bg-card border border-border rounded-2xl shadow-xl overflow-hidden z-50"
                    data-testid="planner-notification-panel"
                >
                    <div className="px-4 py-3 border-b border-border flex items-start justify-between gap-2">
                        <div className="min-w-0">
                            <div className="font-display font-semibold text-sm">Action needed</div>
                            <div className="text-[11px] text-muted-foreground">
                                {liveCount === 0
                                    ? "Nothing waiting on you"
                                    : snoozedNow
                                        ? `${liveCount} item${liveCount === 1 ? "" : "s"} · snoozed`
                                        : `${liveCount} case${liveCount === 1 ? "" : "s"} need your input`}
                            </div>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                            {liveCount > 0 && !snoozedNow && (
                                <button
                                    type="button"
                                    onClick={markAllRead}
                                    className="text-[11px] font-semibold text-primary hover:underline inline-flex items-center gap-1"
                                    data-testid="planner-notification-mark-all-read"
                                    title="Silences the badge for 1 hour. Items still live in your queue."
                                >
                                    <BellSlash size={11} weight="bold" />
                                    Mark all read
                                </button>
                            )}
                            {snoozedNow && (
                                <button
                                    type="button"
                                    onClick={unsnooze}
                                    className="text-[11px] font-semibold text-primary hover:underline"
                                    data-testid="planner-notification-unsnooze"
                                >
                                    Un-snooze
                                </button>
                            )}
                            <button
                                type="button" onClick={() => setOpen(false)}
                                className="text-muted-foreground hover:text-foreground"
                                aria-label="Close"
                            >
                                <X size={14} />
                            </button>
                        </div>
                    </div>

                    {loading && (
                        <div className="px-4 py-6 text-sm text-muted-foreground text-center">Loading…</div>
                    )}

                    {!loading && groups.length === 0 && (
                        <div className="px-4 py-8 text-center" data-testid="planner-notification-empty">
                            <Bell size={28} weight="fill" className="mx-auto text-emerald-500 mb-2" />
                            <div className="text-sm font-semibold">You're all caught up</div>
                            <div className="text-[11px] text-muted-foreground mt-1">When a case lands on your plate it'll pop up here.</div>
                        </div>
                    )}

                    {!loading && groups.length > 0 && (
                        <div className="max-h-[440px] overflow-y-auto" data-testid="planner-notification-groups">
                            {groups.map((g) => {
                                const meta = KIND_META[g.kind] || KIND_META.assigned_to_planner;
                                const isOpen = expanded[g.kind] || false;
                                return (
                                    <div key={g.kind} className="border-b border-border last:border-b-0" data-testid={`planner-notification-group-${g.kind}`}>
                                        <button
                                            type="button"
                                            onClick={() => setExpanded((e) => ({ ...e, [g.kind]: !isOpen }))}
                                            className="w-full px-4 py-3 hover:bg-secondary/40 text-left flex items-center gap-3"
                                            data-testid={`planner-notification-group-${g.kind}-header`}
                                        >
                                            <div className={`w-8 h-8 rounded-full ${meta.chipClass} grid place-items-center shrink-0`}>
                                                <meta.Icon size={14} weight="fill" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="font-semibold text-sm">{meta.label}</div>
                                                <div className="text-[11px] text-muted-foreground mt-0.5">
                                                    {g.items.length} {g.items.length === 1 ? "case" : "cases"} · tap to {isOpen ? "collapse" : "expand"}
                                                </div>
                                            </div>
                                            <span className="text-[10px] tabular-nums font-bold bg-foreground text-card px-1.5 py-0.5 rounded">
                                                {g.items.length}
                                            </span>
                                            {isOpen
                                                ? <CaretUp size={12} className="text-muted-foreground shrink-0" />
                                                : <CaretDown size={12} className="text-muted-foreground shrink-0" />
                                            }
                                        </button>
                                        {isOpen && (
                                            <div className="bg-secondary/30">
                                                {g.items.slice(0, 8).map((n) => (
                                                    <NotificationRow key={n.id} n={n} onNavigate={() => goTo(`/coe/planner/cases/${n.case_id}`)} />
                                                ))}
                                                {g.items.length > 8 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => goTo(meta.target)}
                                                        className="w-full px-4 py-2.5 text-[11px] font-semibold text-primary hover:bg-secondary/50 border-t border-border inline-flex items-center justify-center gap-1"
                                                        data-testid={`planner-notification-group-${g.kind}-see-all`}
                                                    >
                                                        See {g.items.length - 8} more <ArrowRight size={10} />
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {!loading && groups.length > 0 && (
                        <button
                            type="button"
                            onClick={() => goTo("/coe/planner/queue")}
                            className="w-full px-4 py-3 text-xs font-semibold text-primary hover:bg-secondary/50 border-t border-border inline-flex items-center justify-center gap-1"
                            data-testid="planner-notification-view-all"
                        >
                            Open planner queue <ArrowRight size={11} />
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}


function NotificationRow({ n, onNavigate }) {
    const meta = KIND_META[n.kind] || KIND_META.assigned_to_planner;
    return (
        <button
            type="button"
            onClick={onNavigate}
            className="w-full text-left px-4 py-2.5 hover:bg-card border-b last:border-b-0 border-border flex items-start gap-3"
            data-testid={`planner-notification-item-${n.id}`}
        >
            <div className={`w-7 h-7 rounded-full ${meta.chipClass} grid place-items-center shrink-0`}>
                <meta.Icon size={12} weight="fill" />
            </div>
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-semibold text-sm truncate">{n.title}</div>
                    {n.case_code && <span className="font-mono text-[10px] px-1 rounded bg-card text-muted-foreground border border-border">{n.case_code}</span>}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5 truncate">{n.patient_name}</div>
                <div className="text-xs text-foreground/80 mt-1 line-clamp-2">{n.message}</div>
            </div>
        </button>
    );
}
